import SearchField from "@/components/SearchField/SearchField";
import { ViewUnit } from "@/components/ViewUnit/ViewUnit";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { Card, List, Pagination } from "@/components/ui";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { getChaptersByUser } from "@/shared/reducers/entities/edo-chapter.reducer";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { ITEMS_PER_PAGE } from "@/shared/util/pagination.constants";
import { BookOpen, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { IPaginationBaseState } from "react-jhipster";
import { DeleteUnit } from "../courses/components/DeleteUnit";
import { CapitoloRow } from "./components/CapitoloRow/CapitoloRow";
import DeleteChapter from "./components/DeleteChapter/DeleteChapter";
import { EditChapterInfo } from "./components/EditCapitolo/EditChapterInfo";
import { EditChapterUnits } from "./components/EditCapitolo/EditChapterUnits";
import { EditUnit } from "./components/EditUnit/EditUnit";

const ArchivioCapitoli = () => {
  const dispatch = useAppDispatch();
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const {
    entities: edoChapterList,
    loading,
    totalItems,
    updateSuccess,
  } = useAppSelector<EntityState<IEdoChapter>>((state) => state.edoChapter);
  const [paginationState, setPaginationState] = useState<IPaginationBaseState>({
    activePage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
    sort: "updatedAt",
    order: "DESC",
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [editChapterInfoDialog, setEditChapterInfoDialog] = useState<{
    open: boolean;
    entity: IEdoChapter | null;
  }>({ open: false, entity: null });
  const [editChapterUnitsDialog, setEditChapterUnitsDialog] = useState<{
    open: boolean;
    entity: IEdoChapter | null;
  }>({ open: false, entity: null });
  const [deleteChapterDialog, setDeleteChapterDialog] = useState<{
    open: boolean;
    entity: IEdoChapter | null;
  }>({ open: false, entity: null });
  const [viewUnitDialog, setViewUnitDialog] = useState<{
    open: boolean;
    entity: IEdoUnit | null;
  }>({ open: false, entity: null });
  const [deleteUnitDialog, setDeleteUnitDialog] = useState<{
    open: boolean;
    entity: IEdoUnit | null;
  }>({ open: false, entity: null });
  const [editUnitDialog, setEditUnitDialog] = useState<{
    open: boolean;
    entity: IEdoUnit | null;
  }>({ open: false, entity: null });

  const handleCloseEditCapitoloDialog = (updated: boolean) => {
    setEditChapterInfoDialog({ open: false, entity: null });
    if (updated) handleGetEdoCapitoloList();
  };

  const handleCloseEditChapterUnitsDialog = (updated: boolean) => {
    setEditChapterUnitsDialog({ open: false, entity: null });
    if (updated) handleGetEdoCapitoloList();
  };

  const handleGetEdoCapitoloList = () => {
    const filters = new URLSearchParams();
    if (searchValue) filters.append("search", searchValue);
    dispatch(
      getChaptersByUser({
        userId: account.id,
        page: paginationState.activePage,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        ...(filters.toString() ? { filters: filters.toString() } : {}),
      })
    );
  };

  useEffect(() => {
    handleGetEdoCapitoloList();
  }, [
    searchValue,
    updateSuccess,
    paginationState.activePage,
    paginationState.order,
    paginationState.sort,
    paginationState.itemsPerPage,
  ]);

  const handlePagination = (currentPage: number) =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleRowsPerPage = (rowsPerPage: number) =>
    setPaginationState({
      ...paginationState,
      itemsPerPage: rowsPerPage,
      activePage: 1,
    });

  return (
    <>
      <Card className="overflow-hidden rounded-3xl border border-[var(--color-mockup-border)] shadow-card bg-surface p-0">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] p-4 bg-[var(--color-surface-secondary)]/30">
          <div className="w-full sm:max-w-xs">
            <SearchField
              placeholder="Cerca un capitolo"
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                setEditChapterInfoDialog({ open: true, entity: null })
              }
              className="flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--color-mockup-teal)] px-4 py-2 text-xs font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95"
            >
              <Plus className="h-4 w-4" />
              Aggiungi capitolo
            </button>
          </div>
        </div>
        {loading && (
          <div className="flex justify-center py-8">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
        <div className="pt-0">
          {edoChapterList.length ? (
            <List className="divide-y divide-[var(--color-mockup-border)]">
              {edoChapterList.map((chapter, index) => (
                <CapitoloRow
                  key={chapter.id}
                  chapter={chapter}
                  isLastElement={index === edoChapterList.length - 1}
                  handleOpenEditChapterUnitsDialog={(ch) =>
                    setEditChapterUnitsDialog({ open: true, entity: ch })
                  }
                  handleOpenEditCapitoloDialog={(ch) =>
                    setEditChapterInfoDialog({ open: true, entity: ch })
                  }
                  handleOpenViewUnitDialog={(unit) =>
                    setViewUnitDialog({ open: true, entity: unit })
                  }
                  handleOpenDeleteCapitoloDialog={(ch) =>
                    setDeleteChapterDialog({ open: true, entity: ch })
                  }
                />
              ))}
            </List>
          ) : (
            !loading && (
              <div className="px-6 py-12 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <BookOpen className="h-5 w-5 text-primary" />
                </div>
                <p className="text-sm font-medium text-[var(--color-mockup-text-mid)]">
                  Nessun capitolo trovato.
                </p>
              </div>
            )
          )}
        </div>
        <Pagination
          count={totalItems}
          page={paginationState.activePage - 1}
          rowsPerPage={paginationState.itemsPerPage}
          onPageChange={(p) => handlePagination(p + 1)}
          onRowsPerPageChange={handleRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count !== -1 ? count : "more than" + to}`
          }
        />
      </Card>
      <EditChapterInfo
        chapter={editChapterInfoDialog.entity}
        open={editChapterInfoDialog.open}
        handleClose={handleCloseEditCapitoloDialog}
      />
      <EditChapterUnits
        chapter={editChapterUnitsDialog.entity}
        open={editChapterUnitsDialog.open}
        handleClose={handleCloseEditChapterUnitsDialog}
        handleOpenViewUnitDialog={(unit) =>
          setViewUnitDialog({ open: true, entity: unit })
        }
        handleOpenEditUnitDialog={(unit) =>
          setEditUnitDialog({ open: true, entity: unit })
        }
        handleOpenDeleteUnitDialog={(unit) =>
          setDeleteUnitDialog({ open: true, entity: unit })
        }
      />
      <DeleteChapter
        open={deleteChapterDialog.open}
        chapter={deleteChapterDialog.entity}
        onClose={() => setDeleteChapterDialog({ open: false, entity: null })}
      />
      <ViewUnit
        open={viewUnitDialog.open}
        unit={viewUnitDialog.entity}
        handleClose={() => setViewUnitDialog({ open: false, entity: null })}
      />
      <EditUnit
        open={editUnitDialog.open}
        unit={editUnitDialog.entity}
        handleClose={() => setEditUnitDialog({ open: false, entity: null })}
        chapter={editChapterUnitsDialog.entity}
      />
      <DeleteUnit
        open={deleteUnitDialog.open}
        unit={deleteUnitDialog.entity}
        handleClose={(updated) =>
          setDeleteUnitDialog({ open: false, entity: null })
        }
      />
    </>
  );
};

export default ArchivioCapitoli;
